import { z } from 'zod/v3';
import { parseNumber } from '../../../../common/utils/parsers/parseNumber';
import { isOk } from '../../../../common/utils/parsers/result';
import { AlgorithmJoinValidFormDataSchema } from './validFormData/AlgorithmJoinValidFormDataSchema';

export const AlgorithmJoinsMutationDtoSchema = z.object({
  joins: z
    .array(
      AlgorithmJoinValidFormDataSchema.omit({
        algorithmJoinItems: true,
      }).extend({
        algorithmJoinItems: z.array(
          z.discriminatedUnion('_variant', [
            z.object({
              id: z.number().nullish(),
              _variant: z.literal('DIMENSION_DIMENSION'),
              algorithmSourceId: z.number(),
              sourceDimension: z.string(),
              targetDimension: z.string(),
            }),
            z.object({
              id: z.number().nullish(),
              _variant: z.literal('DIMENSION_VALUE'),
              algorithmSourceId: z.number(),
              sourceDimension: z.string(),
              targetValue: z.union([z.string(), z.number()]),
              targetValueFormat: z.string().optional().nullable(),
              targetValueDataType: z.string().optional(),
            }),
            z.object({
              id: z.number().nullish(),
              _variant: z.literal('VALUE_DIMENSION'),
              sourceValue: z.union([z.string(), z.number()]),
              sourceValueFormat: z.string().optional().nullable(),
              sourceValueDataType: z.string().optional(),
              targetDimension: z.string(),
            }),
            z.object({
              id: z.number().nullish(),
              _variant: z.literal('MAPPING_DIMENSION'),
              algorithmMappingAlias: z.string(),
              targetDimension: z.string(),
            }),
            z.object({
              id: z.number().nullish(),
              _variant: z.literal('MAPPING_VALUE'),
              algorithmMappingAlias: z.string(),
              targetValue: z.string(),
            }),
            z.object({
              id: z.number().nullish(),
              _variant: z.literal('DIMENSION_MAPPING'),
              algorithmSourceId: z.number(),
              sourceDimension: z.string(),
              algorithmMappingAlias: z.string(),
            }),
            z.object({
              id: z.number().nullish(),
              _variant: z.literal('VALUE_MAPPING'),
              sourceValue: z.string(),
              algorithmMappingAlias: z.string(),
            }),
          ]),
        ),
      }),
    )
    .transform((joins) =>
      joins.map(({ algorithmJoinItems, ...join }, joinIndex: number) => ({
        ...join,
        sequenceNumber: joinIndex + 1,
        algorithmJoinItems: algorithmJoinItems.map(({ _variant, ...item }) => {
          if (
            'sourceValueDataType' in item &&
            item.sourceValueDataType === 'NUMBER'
          ) {
            item.sourceValue = parseNumberValueAsString(item.sourceValue);
          }
          if (
            'targetValueDataType' in item &&
            item.targetValueDataType === 'NUMBER'
          ) {
            item.targetValue = parseNumberValueAsString(item.targetValue);
          }
          return item;
        }),
      })),
    ),
});

const parseNumberValueAsString = (value: string | number): string => {
  if (typeof value === 'number') {
    return value.toString();
  }

  const result = parseNumber(value);
  if (isOk(result)) {
    return result.value.toString();
  }

  throw new Error('Expecting a valid parseable number value in mutation DTO');
};
